import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, styled, Typography } from '@mui/material';

import Field from '@/components/fields/Field';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';
import NormsDropdownSelect from '@/views/fields/NormsDropdownSelect/NormsDropdownSelect';

import { OrganizationSettingsFieldValues } from './types';

const TestNameBox = styled(Box)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing(1),
  padding: spacing(1),
  border: borders.default,
  borderColor: palette.grey[300],
  borderRadius: spacing(1),
  backgroundColor: palette.background.default
}));

const StyledColumnTitle = styled(Typography)(({ theme: { palette } }) => ({
  color: palette.grey[700],
  fontWeight: typography.fontWeightMedium,
  textAlign: 'start'
}));

const NormsList: FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { watch } = useFormContext<OrganizationSettingsFieldValues>();

  const norms = watch('norms');

  const normsItems = useMemo(() => norms?.items || [], [norms?.items]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 2,
        mx: 3,
        mb: 3,
        maxWidth: 450,
        '& .MuiFormControl-root': { pb: 0 },
        '& .MuiFormLabel-root': { mb: 0 }
      }}
    >
      <StyledColumnTitle variant="body2">{t('orgSettings.norms.test')}</StyledColumnTitle>
      <StyledColumnTitle variant="body2">{t('orgSettings.norms.norm')}</StyledColumnTitle>
      {normsItems.map((norm, index) => (
        <React.Fragment key={norm.testTypeId}>
          <TestNameBox>
            <Typography variant="body3">{norm.testName || ''}</Typography>
          </TestNameBox>
          <Field
            component={NormsDropdownSelect}
            name={`norms.items.${index}.defaultNormId`}
            innerProps={{
              testTypeId: norm.testTypeId,
              placeholder: t('orgSettings.norms.selectNorm')
            }}
            withFormControl
          />
        </React.Fragment>
      ))}
      <TestNameBox>
        <Typography variant="body3">{t('orgSettings.norms.allOtherTests')}</Typography>
      </TestNameBox>
      <Field
        component={NormsDropdownSelect}
        name="norms.defaultNormId"
        innerProps={{
          testTypeId: 'default',
          placeholder: t('orgSettings.norms.selectNorm')
        }}
        withFormControl
      />
    </Box>
  );
};

export default NormsList;
